import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import DatePicker from "react-datepicker";
import Table from "./table";
import DateInput from "./DateInput";
import { NavErrorBanner, NavLoading } from "./status";
import { latestByFundColumns } from "./list_of_columns";
import { groupDigits } from "./cell_formatter";
import { NAV_PATHS, describeError, toApiDate, useNavRequest } from "./api";
import {
  RUN_ACTIONS,
  errorPhase,
  isHandInserted,
  outcomeText,
  resultPhase,
  withFundsWithoutNav
} from "./fundSync";
import Icon from "../../components/Icon";
import ConfirmDialog from "../../components/ConfirmDialog";
import messages from "./messages";

const LATEST_TABLE_MIN_WIDTH = 1920;

// Deleting NAV history is a QA tool for UAT; production NAV is only ever re-synced, never deleted.
const SHOW_DELETE = (process.env.REACT_APP_ENV || "").trim() !== "production";

/**
 * "What is each fund's NAV right now?"
 *
 * The NAV History tab answers the opposite question — every NAV row there has ever been, 128k of
 * them, so the newest row of any one fund is buried among thousands of its own older rows. This
 * view is one row per fund: its newest NAV date, value, change, return type, source and the job
 * that wrote it.
 *
 * The grouping is done in the database, not here. The per-fund read returns 58 rows already
 * reduced (`ARRAY_AGG(... ORDER BY nav_date DESC)[1]` per column, so every value comes from the
 * same newest row); fetching NAV history and grouping in the browser would mean pulling six
 * figures of rows to render fifty-eight.
 *
 * That read takes no filters and no paging, which is why this tab offers a fund-code box that
 * filters the rows it already has rather than a filter bar that pretends to query the server.
 *
 * Each row can also re-sync its fund's NAV history since inception, and outside production delete
 * it. The phases of those runs are described in fundSync.js. The fund master is read alongside the
 * summary so that a fund with no NAV left still has a row, and a Sync button to bring it back.
 */
function LatestByFundTab() {
  const intl = useIntl();
  const requestNav = useNavRequest();

  const [rows, setRows] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [fundCode, setFundCode] = useState("");
  const [run, setRun] = useState(null);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchLatest = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setRequesting(true);
    setError(null);

    // A response that arrives after the operator has pressed refresh again, or after leaving the
    // tab, is dropped rather than written over newer state.
    const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

    try {
      const [summary, master] = await Promise.all([
        requestNav(NAV_PATHS.historySummary),
        // Without the master the tab still shows what it showed before it was read: the summary.
        requestNav(NAV_PATHS.fundMaster).catch(() => null)
      ]);
      if (!isCurrent() || summary.signedOut || (master && master.signedOut)) {
        return;
      }
      setRows(
        withFundsWithoutNav(
          Array.isArray(summary.data) ? summary.data : [],
          master && Array.isArray(master.data) ? master.data : []
        )
      );
    } catch (requestError) {
      if (!isCurrent()) {
        return;
      }
      setRows([]);
      setError(describeError(requestError, intl, messages));
    } finally {
      if (isCurrent()) {
        setRequesting(false);
      }
    }
  }, [requestNav, intl]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  const runAction = useCallback(
    async (action, row, params) => {
      const running = { action, phase: "running", row };
      setRun(running);
      let next;
      try {
        const result = await requestNav(RUN_ACTIONS[action].endpoint, params);
        next = result.signedOut ? null : resultPhase(running, result, intl);
      } catch (runError) {
        next = errorPhase(running, runError, intl);
      }
      // The run can outlast the tab; a late answer is dropped like fetchLatest drops one.
      if (!mountedRef.current) {
        return;
      }
      setRun(next);
      if (next && next.phase === "done") {
        fetchLatest();
      }
    },
    [requestNav, intl, fetchLatest]
  );

  const running = run && run.phase === "running" ? run : null;
  const renderActionsCell = useCallback(
    row => {
      const busy = action =>
        running !== null && running.action === action && running.row.fund_no === row.fund_no;
      return (
        <div className="nav-row-actions">
          <button
            className="nav-row-action-button"
            disabled={running !== null}
            onClick={() => setRun({ action: "sync", phase: "confirm", row })}
          >
            <Icon name="refresh" size={16} />
            {intl.formatMessage(busy("sync") ? messages.buttonSyncing : messages.buttonSync)}
          </button>
          {SHOW_DELETE ? (
            <button
              className="nav-row-action-button nav-row-action-danger"
              disabled={running !== null || !(Number(row.row_count) > 0)}
              onClick={() =>
                setRun({ action: "delete", phase: "confirm", row, dateFrom: null, dateTo: null })
              }
            >
              <Icon name="trash" size={16} />
              {intl.formatMessage(busy("delete") ? messages.buttonDeleting : messages.buttonDelete)}
            </button>
          ) : null}
        </div>
      );
    },
    [running, intl]
  );
  const columns = useMemo(() => latestByFundColumns(intl, renderActionsCell), [
    intl,
    renderActionsCell
  ]);
  const closeRun = () => setRun(null);

  const visibleRows = useMemo(() => {
    const needle = fundCode.trim().toLowerCase();
    if (!needle) {
      return rows;
    }
    return rows.filter(row => `${row.fund_code || ""}`.toLowerCase().includes(needle));
  }, [rows, fundCode]);

  return (
    <React.Fragment>
      {running ? (
        <NavLoading
          text={intl.formatMessage(RUN_ACTIONS[running.action].loading, {
            fundCode: running.row.fund_code
          })}
        />
      ) : requesting ? (
        <NavLoading text={intl.formatMessage(messages.loadingNavHistory)} />
      ) : null}

      {run && run.action === "sync" && run.phase === "confirm" ? (
        <ConfirmDialog
          title={intl.formatMessage(messages.syncConfirmTitle, { fundCode: run.row.fund_code })}
          labelId="nav-sync-confirm-title"
          onDismiss={closeRun}
          actions={[
            {
              label: intl.formatMessage(messages.buttonCancel),
              autoFocus: true,
              onClick: closeRun
            },
            {
              label: intl.formatMessage(messages.buttonSync),
              primary: true,
              onClick: () => runAction("sync", run.row, { fund_no: run.row.fund_no })
            }
          ]}
        >
          {intl.formatMessage(messages.syncConfirmBody, { fundCode: run.row.fund_code })}
        </ConfirmDialog>
      ) : null}

      {run && run.action === "delete" && run.phase === "confirm" ? (
        <ConfirmDialog
          title={intl.formatMessage(messages.deleteConfirmTitle, { fundCode: run.row.fund_code })}
          labelId="nav-delete-confirm-title"
          onDismiss={closeRun}
          actions={[
            {
              label: intl.formatMessage(messages.buttonCancel),
              autoFocus: true,
              onClick: closeRun
            },
            {
              label: intl.formatMessage(messages.buttonDelete),
              primary: true,
              onClick: () =>
                runAction("delete", run.row, {
                  fund_no: run.row.fund_no,
                  date_from: toApiDate(run.dateFrom),
                  date_to: toApiDate(run.dateTo)
                })
            }
          ]}
        >
          <p>{intl.formatMessage(messages.deleteConfirmBody, { fundCode: run.row.fund_code })}</p>
          {isHandInserted(run.row) ? (
            <p className="nav-delete-warning">
              {intl.formatMessage(messages.deleteHandInsertedWarning, {
                fundCode: run.row.fund_code,
                syncBy: run.row.last_sync_by
              })}
            </p>
          ) : null}
          <div className="nav-delete-range">
            <div className="nav-delete-range-field">
              <span className="nav-filter-label">
                {intl.formatMessage(messages.deleteDateFrom)}
              </span>
              <DatePicker
                selected={run.dateFrom}
                onChange={date => setRun({ ...run, dateFrom: date })}
                maxDate={run.dateTo}
                isClearable
                dateFormat="dd/MM/yy"
                customInput={<DateInput />}
              />
            </div>
            <div className="nav-delete-range-field">
              <span className="nav-filter-label">{intl.formatMessage(messages.deleteDateTo)}</span>
              <DatePicker
                selected={run.dateTo}
                onChange={date => setRun({ ...run, dateTo: date })}
                minDate={run.dateFrom}
                isClearable
                dateFormat="dd/MM/yy"
                customInput={<DateInput />}
              />
            </div>
          </div>
        </ConfirmDialog>
      ) : null}

      {run && (run.phase === "done" || run.phase === "failed") ? (
        <ConfirmDialog
          title={intl.formatMessage(
            run.phase === "done"
              ? RUN_ACTIONS[run.action].doneTitle
              : RUN_ACTIONS[run.action].failedTitle
          )}
          labelId="nav-run-result-title"
          onDismiss={closeRun}
          actions={[
            {
              label: intl.formatMessage(messages.buttonClose),
              primary: true,
              autoFocus: true,
              onClick: closeRun
            }
          ]}
        >
          {outcomeText(run, intl)}
        </ConfirmDialog>
      ) : null}

      <div className="nav-filter-bar">
        <div className="nav-filter-field">
          <span className="nav-filter-label">{intl.formatMessage(messages.filterFundCode)}</span>
          <div className="nav-search">
            <Icon name="search" size={18} />
            <input
              className="nav-search-bar"
              type="text"
              value={fundCode}
              placeholder={intl.formatMessage(messages.filterFundCodePlaceholder)}
              onChange={event => setFundCode(event.target.value)}
            />
          </div>
        </div>

        <div className="nav-filter-actions">
          <button className="nav-search-button" onClick={() => fetchLatest()}>
            <Icon name="refresh" size={18} />
            {intl.formatMessage(messages.refresh)}
          </button>
          <button className="nav-clear-button" onClick={() => setFundCode("")}>
            <Icon name="close" size={18} />
            {intl.formatMessage(messages.buttonClear)}
          </button>
        </div>
      </div>

      <NavErrorBanner message={error} onRetry={fetchLatest} />

      <p className="nav-total-rows">
        {intl.formatMessage(messages.latestByFundCaption, {
          funds: groupDigits(visibleRows.length)
        })}
      </p>

      <Table
        columns={columns}
        data={visibleRows}
        errorMessage={error}
        onRetry={fetchLatest}
        tableMinWidth={LATEST_TABLE_MIN_WIDTH}
      />
    </React.Fragment>
  );
}

export default LatestByFundTab;
